import { useLayoutEffect, type RefObject } from 'react';

/** Whether the row's last item has wrapped onto a line below its first. */
function wraps(row: HTMLElement): boolean {
  const first = row.firstElementChild;
  const last = row.lastElementChild;
  if (!(first instanceof HTMLElement) || !(last instanceof HTMLElement)) return false;
  // Hidden, as the tools are while dictating, it's on no line at all.
  if (!last.offsetParent) return false;
  return last.offsetTop >= first.offsetTop + first.offsetHeight;
}

/** Takes the fewest steps that bring the row onto one line, or every step there is. */
function fit(row: HTMLElement, steps: readonly string[]): void {
  let taken = 0;
  row.dataset.fit = '';
  while (taken < steps.length && wraps(row)) {
    taken += 1;
    row.dataset.fit = steps.slice(0, taken).join(' ');
  }
}

/**
 * Keeps a wrapping row of tools on one line: while its last item wraps below its first, it takes
 * the next of `steps`, a word added to the row's `data-fit`, which the row's styles answer by
 * making it narrower. Labels come and go with the language, and fonts load late, so it fits again
 * whenever the row's content or width changes, rather than at set widths.
 */
export function useOneLine(ref: RefObject<HTMLElement | null>, steps: readonly string[]): void {
  useLayoutEffect(() => {
    const row = ref.current;
    if (!row) return;
    fit(row, steps);
    // Fitting changes the row's height when it unwraps, and acting on that at once would loop.
    let frame = 0;
    const refit = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        fit(row, steps);
      });
    };
    const resizes = new ResizeObserver(refit);
    resizes.observe(row);
    // Changes React makes inside the row, such as a label in another language or a tool at work,
    // land before the next paint.
    const mutations = new MutationObserver(() => {
      fit(row, steps);
    });
    mutations.observe(row, { childList: true, characterData: true, subtree: true });
    const fonts = typeof document.fonts === 'undefined' ? undefined : document.fonts;
    fonts?.addEventListener('loadingdone', refit);
    return () => {
      cancelAnimationFrame(frame);
      resizes.disconnect();
      mutations.disconnect();
      fonts?.removeEventListener('loadingdone', refit);
    };
  }, [ref, steps]);
}
